import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
  showApiError,
  showSuccess,
  showWarning,
  clearAuthStorage,
} from "@/utils";
import { DEFAULT_AVATAR_URL } from "@/const";
import PasswordInput from "@/components/ui/PasswordInput";
import PasswordRequirements from "@/components/ui/PasswordRequirements";
import "./page.css";

// Helper component for password requirement indicator
function UserProfile() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [userInfo, setUserInfo] = useState({
    username: "",
    firstname: "",
    lastname: "",
    client_id: "",
    profile_picture: "",
    email: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);

  // Form states
  const [editProfileForm, setEditProfileForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Profile picture upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);

  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    maxLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  // Error states for password fields
  const [passwordErrors, setPasswordErrors] = useState({
    oldPassword: "",
    newPassword: "",
  });

  useEffect(() => {
    const username = getLocalStorageWithExpiry("username");
    if (!username) {
      setIsLoading(false);
      return;
    }

    const fetchUserInfo = async (username) => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${BACKEND_URL}/api/v1/user/${username}`,
          {
            withCredentials: true,
          },
        );

        if (response.data.success) {
          setUserInfo(response.data);
          setEditProfileForm({
            firstname: response.data.firstname,
            lastname: response.data.lastname,
            email: response.data.email || "",
          });
        } else {
          setError("Failed to load user information");
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        if (error.response?.status === 401) {
          clearAuthStorage();
          navigate("/login");
        } else {
          setError("Failed to load user information");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo(username);
  }, [navigate, BACKEND_URL]);

  // Validate password requirements in real-time
  useEffect(() => {
    const password = passwordForm.newPassword;

    setPasswordValidation({
      minLength: password.length >= 8,
      maxLength: password.length <= 20,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });

    // Clear old password error when new password changes
    if (passwordForm.newPassword) {
      setPasswordErrors((prev) => ({ ...prev, oldPassword: "" }));
    }

    // Set error if new password exceeds max length
    if (password.length > 20) {
      setPasswordErrors((prev) => ({
        ...prev,
        newPassword: "Password must not exceed 20 characters",
      }));
    } else {
      setPasswordErrors((prev) => ({ ...prev, newPassword: "" }));
    }
  }, [passwordForm.newPassword]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      showApiError(
        t(
          "userProfile.onlyImageFilesAllowed",
          "Only image files are allowed (JPG, PNG, GIF, WebP)",
        ),
      );
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showApiError(
        t(
          "userProfile.fileSizeLimit",
          "File size must be less than 5MB",
        ),
      );
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleProfilePictureUpload = async () => {
    if (!selectedFile) {
      showWarning(
        t("userProfile.selectFileToUpload", "Please select a file to upload"),
      );
      return;
    }

    setIsUploadingPicture(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/user/upload-profile-picture`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        // Update user info with new profile picture from GCS
        setUserInfo((prev) => ({
          ...prev,
          profile_picture: response.data.gs_link,
        }));

        setLocalStorageWithExpiry("profile_picture", response.data.gs_link, 60);

        // Clear selected file and preview
        setSelectedFile(null);
        setPreviewUrl(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Close modal
        setShowProfilePictureModal(false);

        showSuccess(
          t(
            "userProfile.avatarUpdatedSuccess",
            "Profile picture updated successfully!",
          ),
        );
      } else {
        showApiError(
          response.data.message ||
            t("userProfile.uploadPictureFailed", "Failed to upload profile picture"),
        );
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      if (error.response?.data?.detail) {
        showApiError(error.response.data.detail);
      } else {
        showApiError(
          t("userProfile.uploadPictureFailed", "Failed to upload profile picture"),
        );
      }
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/v1/user/${userInfo.username}`,
        {
          ...editProfileForm,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success) {
        setUserInfo((prev) => ({
          ...prev,
          ...editProfileForm,
        }));

        setLocalStorageWithExpiry("firstname", editProfileForm.firstname, 60);
        setLocalStorageWithExpiry("lastname", editProfileForm.lastname, 60);

        setShowEditProfileModal(false);
        showSuccess(
          t("userProfile.profileUpdatedSuccess", "Profile updated successfully!"),
        );
      } else {
        showApiError(
          response.data.message ||
            t("userProfile.updateProfileFailed", "Failed to update profile"),
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showApiError(
        t("userProfile.updateProfileFailed", "Failed to update profile"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setPasswordErrors({ oldPassword: "", newPassword: "" });

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showApiError(
        t("userProfile.passwordsDoNotMatch", "New passwords do not match"),
      );
      return;
    }

    const password = passwordForm.newPassword;

    // Check max length
    if (password.length > 20) {
      showApiError(
        t(
          "userProfile.passwordMaxLength",
          "Password must not exceed 20 characters",
        ),
      );
      return;
    }

    const passwordPolicyRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordPolicyRegex.test(password)) {
      showApiError(
        t(
          "userProfile.passwordPolicy",
          "New password must be at least 8 characters long and include uppercase, lowercase, number, and special character",
        ),
      );
      return;
    }

    if (!passwordForm.oldPassword) {
      showWarning(
        t(
          "userProfile.enterCurrentPassword",
          "Please enter your current password",
        ),
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/user/update-password`,
        {
          old_password: passwordForm.oldPassword,
          new_password: passwordForm.newPassword,
        },
        {
          withCredentials: true,
        },
      );

      if (response.data.success) {
        setShowUpdatePasswordModal(false);
        setPasswordForm({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        showSuccess(
          t(
            "userProfile.passwordUpdatedAndLogin",
            "Password updated successfully! Please log in again with your new password.",
          ),
        );

        // Clear localStorage and redirect to login
        setTimeout(() => {
          clearAuthStorage();
          navigate("/login");
        }, 2500);
      } else {
        // Handle backend error messages
        if (response.data.message === "Invalid old password") {
          setPasswordErrors((prev) => ({
            ...prev,
            oldPassword: "The current password you entered is incorrect",
          }));
        } else if (
          response.data.message ===
          "New password must be different from old password"
        ) {
          setPasswordErrors((prev) => ({
            ...prev,
            newPassword: response.data.message,
          }));
        }
      }
    } catch (error) {
      console.error("Error updating password:", error);
      // Check for specific error messages from backend
      if (error.response?.data?.message === "Invalid old password") {
        setPasswordErrors((prev) => ({
          ...prev,
          oldPassword: "The current password you entered is incorrect",
        }));
      } else if (
        error.response?.data?.message ===
        "New password must be different from old password"
      ) {
        setPasswordErrors((prev) => ({
          ...prev,
          newPassword: error.response.data.message,
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  const handleProfilePictureModalClose = () => {
    setShowProfilePictureModal(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"profile"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {/* Heading */}
          <h3 className="h4 fw-bold text-dark mb-0">
            {t("userProfile.title", "User Profile")}
          </h3>
          <p className="mt-1 fs-6 text-secondary">
            {t(
              "userProfile.subtitle",
              "Manage your account information and settings",
            )}
          </p>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Loading State */}
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  {t("common.loading", "Loading...")}
                </span>
              </div>
            </div>
          ) : !error ? (
            <div className="row">
              {/* Profile Information Card */}
              <div className="col-md-8">
                <div
                  className="bg-white p-4 card-radius border shadow-sm"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div className="row">
                    <div className="col-md-4 text-center mb-4">
                      <div className="position-relative d-inline-block">
                        <img
                          src={userInfo.profile_picture || DEFAULT_AVATAR_URL}
                          alt="Profile"
                          className="rounded-circle mb-3"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                          }}
                        />
                      </div>

                      {/* Update Profile Picture Button */}
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => setShowProfilePictureModal(true)}
                        >
                          <i className="bi bi-camera me-1"></i>
                          {t(
                            "userProfile.updateAvatar",
                            "Update Profile Picture",
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="col-md-8">
                      <h4 className="fw-bold text-dark mb-3">
                        {userInfo.firstname} {userInfo.lastname}
                      </h4>

                      <div className="row mb-3">
                        <div className="col-sm-4 text-muted">
                          {t("userProfile.email", "Email")}:
                        </div>
                        <div className="col-sm-8 fw-medium">
                          {userInfo.email || (
                            <span className="text-muted">
                              {t("common.notSet", "not set")}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-sm-4 text-muted">
                          {t("userProfile.firstName", "First Name")}:
                        </div>
                        <div className="col-sm-8 fw-medium">
                          {userInfo.firstname}
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-sm-4 text-muted">
                          {t("userProfile.lastName", "Last Name")}:
                        </div>
                        <div className="col-sm-8 fw-medium">
                          {userInfo.lastname}
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-sm-4 text-muted">
                          {t("userProfile.clientId", "Client ID")}:
                        </div>
                        <div className="col-sm-8 fw-medium font-monospace">
                          {userInfo.client_id}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex gap-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowEditProfileModal(true)}
                        >
                          <i className="bi bi-person-gear me-2"></i>
                          {t("userProfile.editProfile", "Edit Profile")}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowUpdatePasswordModal(true)}
                        >
                          <i className="bi bi-key me-2"></i>
                          {t("userProfile.changePassword", "Change Password")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {/* Profile Picture Modal */}
      {showProfilePictureModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("userProfile.updateAvatar", "Update Profile Picture")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleProfilePictureModalClose}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <div className="modal-body">
                {/* Current/Preview Image */}
                <div className="text-center mb-4">
                  <img
                    src={
                      previewUrl ||
                      userInfo.profile_picture ||
                      DEFAULT_AVATAR_URL
                    }
                    alt="Profile Preview"
                    className="rounded-circle mb-3"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                    }}
                  />
                  {previewUrl && (
                    <div className="badge bg-warning text-dark mb-2">
                      {t("common.preview", "Preview")}
                    </div>
                  )}
                </div>

                {/* File Upload Section */}
                <div className="mb-3">
                  <label htmlFor="profilePictureInput" className="form-label">
                    {t("userProfile.uploadNewPicture", "Choose New Profile Picture")}
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="form-control"
                    id="profilePictureInput"
                  />
                  <div className="form-text">
                    {t(
                      "userProfile.fileSizeHint",
                      "Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP",
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleProfilePictureModalClose}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{
                    backgroundColor: "var(--btn-solid-bg)",
                    color: "var(--btn-solid-text)",
                  }}
                  onClick={handleProfilePictureUpload}
                  disabled={!selectedFile || isUploadingPicture}
                >
                  {isUploadingPicture ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                      ></div>
                      {t("assistant.uploading", "Uploading...")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      {t("userProfile.updateAvatar", "Upload Picture")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfileModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("userProfile.editProfile", "Edit Profile")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEditProfileModal(false)}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <form onSubmit={handleEditProfile}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="editFirstname" className="form-label">
                      {t("userProfile.firstName", "First Name")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editFirstname"
                      value={editProfileForm.firstname}
                      onChange={(e) =>
                        setEditProfileForm((prev) => ({
                          ...prev,
                          firstname: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editLastname" className="form-label">
                      {t("userProfile.lastName", "Last Name")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editLastname"
                      value={editProfileForm.lastname}
                      onChange={(e) =>
                        setEditProfileForm((prev) => ({
                          ...prev,
                          lastname: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditProfileModal(false)}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{
                      backgroundColor: "var(--btn-solid-bg)",
                      color: "var(--btn-solid-text)",
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? t("common.editing", "Updating...")
                      : t("userProfile.saveChanges", "Save Changes")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update Password Modal */}
      {showUpdatePasswordModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("userProfile.changePassword", "Change Password")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowUpdatePasswordModal(false);
                    setPasswordForm({
                      oldPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                    setPasswordErrors({ oldPassword: "", newPassword: "" });
                  }}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <form onSubmit={handleUpdatePassword}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="currentPassword" className="form-label">
                      {t("userProfile.currentPassword", "Current Password")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <PasswordInput
                      className={`form-control ${passwordErrors.oldPassword ? "is-invalid" : ""}`}
                      id="currentPassword"
                      value={passwordForm.oldPassword}
                      onChange={(e) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          oldPassword: e.target.value,
                        }))
                      }
                      required
                    />
                    {passwordErrors.oldPassword && (
                      <div className="invalid-feedback d-block">
                        <i
                          className="bi bi-x-circle-fill me-1"
                          aria-hidden="true"
                        ></i>
                        {passwordErrors.oldPassword}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">
                      {t("userProfile.newPassword", "New Password")}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <PasswordInput
                      className={`form-control ${passwordErrors.newPassword ? "is-invalid" : ""}`}
                      id="newPassword"
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          newPassword: e.target.value,
                        }))
                      }
                      required
                      minLength={8}
                      maxLength={20}
                    />
                    {passwordErrors.newPassword && (
                      <div className="invalid-feedback d-block">
                        <i
                          className="bi bi-x-circle-fill me-1"
                          aria-hidden="true"
                        ></i>
                        {passwordErrors.newPassword}
                      </div>
                    )}
                    {passwordForm.newPassword && (
                      <PasswordRequirements
                        password={passwordForm.newPassword}
                      />
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="confirmNewPassword" className="form-label">
                      {t(
                        "userProfile.confirmNewPassword",
                        "Confirm New Password",
                      )}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <PasswordInput
                      id="confirmNewPassword"
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                      required
                      minLength={8}
                      maxLength={20}
                    />
                    {passwordForm.confirmPassword &&
                      passwordForm.newPassword !==
                        passwordForm.confirmPassword && (
                        <div className="form-text text-danger mt-2">
                          <i
                            className="bi bi-x-circle-fill me-1"
                            aria-hidden="true"
                          ></i>
                          <span className="visually-hidden">
                            {t("common.error", "Error")}:{" "}
                          </span>
                          {t("auth.passwordsDoNotMatch", "Passwords do not match")}
                        </div>
                      )}
                    {passwordForm.confirmPassword &&
                      passwordForm.newPassword ===
                        passwordForm.confirmPassword && (
                        <div className="form-text text-success mt-2">
                          <i
                            className="bi bi-check-circle-fill me-1"
                            aria-hidden="true"
                          ></i>
                          <span className="visually-hidden">
                            {t("common.success", "Success")}:{" "}
                          </span>
                          {t("common.passwordsMatch", "Passwords match")}
                        </div>
                      )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowUpdatePasswordModal(false);
                      setPasswordForm({
                        oldPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                      setPasswordErrors({ oldPassword: "", newPassword: "" });
                    }}
                  >
                    {t("common.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    disabled={
                      isSubmitting ||
                      !passwordForm.oldPassword ||
                      !passwordForm.newPassword ||
                      !passwordForm.confirmPassword ||
                      passwordForm.newPassword !==
                        passwordForm.confirmPassword ||
                      !passwordValidation.minLength ||
                      !passwordValidation.maxLength ||
                      !passwordValidation.hasUppercase ||
                      !passwordValidation.hasLowercase ||
                      !passwordValidation.hasNumber ||
                      !passwordValidation.hasSpecialChar
                    }
                    style={{
                      backgroundColor: "var(--btn-solid-bg)",
                      color: "var(--btn-solid-text)",
                    }}
                  >
                    {isSubmitting
                      ? t("common.editing", "Updating...")
                      : t("userProfile.changePassword", "Change Password")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
